import useGameState, {
  GameAction,
  useGameActionDispatch,
} from "app/game/useGameState";
import { GODOT_EVENT, useGodotListener } from "app/godot-embed/godot-bridge";
import { useCallback } from "react";
import { type TutorialLevel } from "./tutorial-data";
import useTutorialState, {
  TutorialAction,
  useTutorialActionDispatch,
} from "./useTutorialState";
import mixpanel from "mixpanel-browser";

export default function useTutorialLevel() {
  const { id: game_id } = useGameState();
  const tutorialDispatch = useTutorialActionDispatch();
  const gameDispatch = useGameActionDispatch();
  const {
    phaseIndex: tutorialPhaseIndex,
    levelData: tutorialData,
    isInTutorial,
    phaseData: tutorialPhase,
  } = useTutorialState();

  const start = useCallback(
    (levelData: TutorialLevel) => {
      mixpanel.track("Pixellence: Tutorial Level Start", {
        game_id,
        level_name: levelData.name,
      });
      gameDispatch({
        type: GameAction.TUTORIAL_LEVEL_START,
      });
      tutorialDispatch({
        type: TutorialAction.START_TUTORIAL_LEVEL,
        payload: { levelData },
      });
    },
    [gameDispatch, tutorialDispatch, game_id],
  );

  const advancePhase = useCallback(() => {
    // If all phases of the current tutorial have been completed, end it.
    if (tutorialPhaseIndex === (tutorialData?.phases.length ?? 1) - 1) {
      const levelName = tutorialData?.name ?? "UNKNOWN";
      mixpanel.track("Pixellence: Tutorial Level Complete", {
        game_id,
        level_name: levelName,
      });
      tutorialDispatch({ type: TutorialAction.FINISH_TUTORIAL });
      gameDispatch({
        type: GameAction.TUTORIAL_LEVEL_FINISH,
        payload: { levelName },
      });
      return;
    }

    tutorialDispatch({ type: TutorialAction.ADVANCE_TUTORIAL });
  }, [
    tutorialDispatch,
    tutorialPhaseIndex,
    tutorialData,
    gameDispatch,
    game_id,
  ]);

  useGodotListener(GODOT_EVENT.TUTORIAL_PHASE_COMPLETED, advancePhase);

  // Advance tutorial when the puzzle is solved
  const handleTutorialSolved = useCallback(() => {
    if (isInTutorial && tutorialPhase?.waitFor === "puzzleComplete") {
      advancePhase();
    }
  }, [isInTutorial, tutorialPhase, advancePhase]);

  useGodotListener(GODOT_EVENT.TUTORIAL_SOLVED, handleTutorialSolved);

  return { start, advancePhase };
}
